import { differenceInMilliseconds, format } from "date-fns";

import type { ExtendedClassSessionSummaryType } from "api/api-data-types";

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

/** Minutes of presence required to count as "attended" (isStudentPresent). */
export const PRESENCE_MINUTES_THRESHOLD = 5;

/** Minimum duration (minutes) for a session to appear in the session log. */
export const MIN_DURATION_CLASS_MINUTES = 5;

/** Minimum duration (minutes) for a session to qualify for engagement metrics. */
export const MIN_QUALIFYING_DURATION_MINUTES = 30;

/** Minimum attendance percentage (0–1) for a session to qualify for engagement metrics. */
export const MIN_QUALIFYING_ATTENDANCE_PERCENTAGE = 0.25;

/** Min qualifying sessions before attendance affects risk level in engagement table. */
export const MIN_QUALIFYING_SESSIONS_FOR_ATTENDANCE_RISK = 4;

/** Attendance threshold (0–1) below which risk increases by one level. */
export const ATTENDANCE_THRESHOLD_FOR_RISK_INCREASE = 0.7;

/** Attendance threshold (0–1) for medium risk when no effort/performance data. */
export const ATTENDANCE_THRESHOLD_FOR_MEDIUM_RISK = 0.85;

// -----------------------------------------------------------------------------
// Types & functions
// -----------------------------------------------------------------------------

export type AttendanceStatsType = {
  averageAttendance: number;
  highestAttendance: { percentage: number; date: string };
};

/** Compute attendance stats from ExtendedClassSessionSummaryType. */
export function computeAttendanceStats(
  extendedSummaries: ExtendedClassSessionSummaryType[],
): AttendanceStatsType {
  const result = {
    averageAttendance: 0,
    highestAttendance: { percentage: 0, date: "" },
  };
  if (extendedSummaries.length === 0) return result;

  let totalAttendance = 0;
  let attendanceSessionCount = 0;

  for (const session of extendedSummaries) {
    const attendancePercentage = session.attendance_percentage;
    totalAttendance += attendancePercentage;
    attendanceSessionCount += 1;
    if (attendancePercentage > result.highestAttendance.percentage && session.started_at) {
      result.highestAttendance = {
        percentage: Math.round(attendancePercentage),
        date: format(session.started_at, "MMM d, yyyy"),
      };
    }
  }

  result.averageAttendance =
    attendanceSessionCount > 0 ? Math.round(totalAttendance / attendanceSessionCount) : 0;
  return result;
}

type StudentTalkTimeStatsType = {
  averageStudentTalkTime: number;
  highestStudentTalkTime: { percentage: number; date: string };
};

/** Compute student talk time stats from ExtendedClassSessionSummaryType. */
export function computeStudentTalkTimeStats(
  extendedSummaries: ExtendedClassSessionSummaryType[],
): StudentTalkTimeStatsType {
  const result = {
    averageStudentTalkTime: 0,
    highestStudentTalkTime: { percentage: 0, date: "" },
  };
  if (extendedSummaries.length === 0) return result;

  let totalTalkTimePct = 0;
  let talkTimeSessionCount = 0;

  for (const session of extendedSummaries) {
    const studentSeconds = session.student_talk_time_seconds ?? 0;
    const instructorSeconds = session.instructor_talk_time_seconds ?? 0;
    const talkTimePercentage = getStudentTalkTimePercentage(studentSeconds, instructorSeconds);

    totalTalkTimePct += talkTimePercentage;
    talkTimeSessionCount += 1;

    if (talkTimePercentage > result.highestStudentTalkTime.percentage) {
      result.highestStudentTalkTime = {
        percentage: Math.round(talkTimePercentage),
        date: format(session.started_at, "MMM d, yyyy"),
      };
    }
  }

  result.averageStudentTalkTime =
    talkTimeSessionCount > 0 ? Math.round(totalTalkTimePct / talkTimeSessionCount) : 0;
  return result;
}

export function isStudentPresent(presenceMinutes: number | null | undefined): boolean {
  return !!presenceMinutes && presenceMinutes > PRESENCE_MINUTES_THRESHOLD;
}

export type SessionWithDurationType = {
  effective_ended_at: string;
  started_at: string;
};

export function getClassSessionDurationInMs(session: SessionWithDurationType): number {
  return differenceInMilliseconds(session.effective_ended_at, session.started_at);
}

export function getClassSessionDurationInMinutes(session: SessionWithDurationType): number {
  return Math.round(getClassSessionDurationInMs(session) / 60000);
}

/**
 * Student share of total classroom talk time (student + instructor).
 * Instructor share is 100 - student when total > 0 so the two always sum to 100.
 */
export function getStudentTalkTimePercentage(
  studentTalkTimeSeconds: number,
  instructorTalkTimeSeconds: number,
): number {
  const total = studentTalkTimeSeconds + instructorTalkTimeSeconds;
  if (total <= 0) return 0;
  return Math.floor((studentTalkTimeSeconds / total) * 100);
}

export function getInstructorTalkTimePercentage(
  studentTalkTimeSeconds: number,
  instructorTalkTimeSeconds: number,
): number {
  const total = studentTalkTimeSeconds + instructorTalkTimeSeconds;
  if (total <= 0) return 0;
  return 100 - getStudentTalkTimePercentage(studentTalkTimeSeconds, instructorTalkTimeSeconds);
}
